import {
  APP_PREFIX_PATH,
  AUTH_PREFIX_PATH,
  HOME_PREFIX_PATH,
} from "configs/AppConfig";
import React from "react";

export const publicRoutes = [
  {
    key: "login",
    path: `${AUTH_PREFIX_PATH}/login`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/login")
    ),
  },
  {
    key: "forgot-password",
    path: `${AUTH_PREFIX_PATH}/forgot-password`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/forgot-password")
    ),
  },
];

export const protectedRoutes = [
  {
    key: "reset-password",
    path: `${AUTH_PREFIX_PATH}/reset-password`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/reset-password")
    ),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: "adminpage",
    path: `${APP_PREFIX_PATH}/adminpage`,
    component: React.lazy(() => import("views/app-views/adminpage")),
    meta: {
      blankLayout: true,
    },
  },
  // ADMIN CONSOLE V2 (flag REACT_APP_ADMIN_V2): additive lane behind a build-time flag —
  // registered ONLY when the flag is the literal "true". Every screen it eventually
  // replaces (pages/setting/*, pages/user-list, apps/roles, apps/clients,
  // apps/accounts/accounts, adminpage, and the auth views) stays registered above and
  // below this block and is byte-untouched, so the two can be run side by side. Retiring
  // those routes is a separate, sequenced cutover — the one recorded production
  // regression on this surface was caused by retiring a screen. Same conditional-spread
  // pattern the ops-v2 and search-v2 lanes used while they were gated.
  ...(process.env.REACT_APP_ADMIN_V2 === "true"
    ? [
        {
          key: "admin.console-v2",
          path: `${APP_PREFIX_PATH}/admin-v2`,
          component: React.lazy(() => import("views/app-views/admin-v2")),
        },
      ]
    : []),
  // The Ops Dashboard is now the only dashboard. v1 (dashboards/faxes) was retired; its
  // path redirects so existing bookmarks keep working.
  {
    key: "dashboard.ops",
    path: `${APP_PREFIX_PATH}/dashboards/ops`,
    component: React.lazy(() => import("views/app-views/dashboards/ops")),
  },
  {
    key: "dashboard.faxes",
    path: `${APP_PREFIX_PATH}/dashboards/faxes`,
    component: React.lazy(() => import("views/app-views/dashboards/redirect-to-ops")),
  },
  {
    key: "apps.checkin.scrumboard",
    path: `${APP_PREFIX_PATH}/apps/checkin/incomingRequest`,
    component: React.lazy(() =>
      import("views/app-views/apps/checkin/scrumboard")
    ),
  },
  {
    key: "apps.project.scrumboard",
    path: `${APP_PREFIX_PATH}/apps/project/incomingRequest`,
    component: React.lazy(() =>
      import("views/app-views/apps/project/scrumboard")
    ),
  },
  {
    key: "apps.referral.scrumboard",
    path: `${APP_PREFIX_PATH}/apps/referral/incomingRequest`,
    component: React.lazy(() =>
      import("views/app-views/apps/referral/scrumboard")
    ),
  },
  // INCOMING REQUESTS V2 (flag REACT_APP_INCOMING_V2): additive lane behind a build-time
  // flag — registered ONLY when the flag is the literal "true". The v1 board above
  // (apps.project.scrumboard, /apps/project/incomingRequest) stays the default and is
  // byte-untouched, so coordinators can run the two side by side and compare. Same
  // conditional-spread pattern the ops-v2 and search-v2 lanes used while they were gated.
  ...(process.env.REACT_APP_INCOMING_V2 === "true"
    ? [
        {
          key: "apps.incoming-v2",
          path: `${APP_PREFIX_PATH}/apps/incoming-v2`,
          component: React.lazy(() => import("views/app-views/apps/incoming-v2")),
        },
      ]
    : []),
  {
    key: "apps.appointment.details.list",
    path: `${APP_PREFIX_PATH}/apps/appointmentDetails/list`,
    component: React.lazy(() =>
      import("views/app-views/apps/appointmentDetails/project-list/ProjectList")
    ),
  },
  {
    key: "apps.faxes",
    path: `${APP_PREFIX_PATH}/apps/faxes`,
    component: React.lazy(() => import("views/app-views/apps/faxes/faxes")),
  },
  // Search v2 is now the only search. The legacy page was retired, so the lane is
  // unconditional — the REACT_APP_SEARCH_V2 build flag is gone, since a flag-off state
  // would leave the app with no search at all. /apps/search redirects so existing
  // bookmarks keep working, exactly as the ops-v2 lane did when v1 was retired.
  {
    key: "apps.search-v2",
    path: `${APP_PREFIX_PATH}/apps/search-v2`,
    component: React.lazy(() => import("views/app-views/apps/search-v2")),
  },
  {
    key: "apps.search",
    path: `${APP_PREFIX_PATH}/apps/search`,
    component: React.lazy(() =>
      import("views/app-views/apps/search-v2/redirect-to-search-v2")
    ),
  },
  {
    key: "apps.sms",
    path: `${APP_PREFIX_PATH}/apps/sms/*`,
    component: React.lazy(() => import("views/app-views/apps/sms")),
  },
  {
    key: "apps.accounts",
    path: `${APP_PREFIX_PATH}/apps/accounts`,
    component: React.lazy(() =>
      import("views/app-views/apps/accounts/accounts")
    ),
  },
  {
    key: "apps.roles",
    path: `${APP_PREFIX_PATH}/apps/roles`,
    component: React.lazy(() => import("views/app-views/apps/roles")),
  },
  {
    key: "apps.agents",
    path: `${APP_PREFIX_PATH}/apps/agents`,
    component: React.lazy(() => import("views/app-views/apps/agents")),
  },
  {
    key: "apps.calls-history",
    path: `${APP_PREFIX_PATH}/apps/calls-history`,
    component: React.lazy(() => import("views/app-views/apps/calls-history")),
  },
  {
    key: "apps.invoice",
    path: `${APP_PREFIX_PATH}/apps/invoice`,
    component: React.lazy(() => import("views/app-views/apps/invoice")),
  },
  {
    key: "apps.clients",
    path: `${APP_PREFIX_PATH}/apps/clients`,
    component: React.lazy(() => import("views/app-views/apps/clients")),
  },
  {
    key: "apps.providers",
    path: `${APP_PREFIX_PATH}/apps/providers`,
    component: React.lazy(() => import("views/app-views/apps/providers")),
  },
  {
    key: "pages.user-list",
    path: `${APP_PREFIX_PATH}/pages/user-list`,
    component: React.lazy(() => import("views/app-views/pages/user-list")),
  },
  {
    key: "pages.setting",
    path: `${APP_PREFIX_PATH}/pages/setting/*`,
    component: React.lazy(() => import("views/app-views/pages/setting")),
  },
  {
    key: "pages.profile",
    path: `${APP_PREFIX_PATH}/pages/profile`,
    component: React.lazy(() => import("views/app-views/pages/profile")),
  },
  {
    key: "pages.imaging-locations",
    path: `${APP_PREFIX_PATH}/pages/imaging-locations`,
    component: React.lazy(() => import("views/app-views/pages/imaging-locations")),
  },
];

export const homeRoutes = [
  {
    key: "lock-screen",
    path: `${AUTH_PREFIX_PATH}/lock-screen`,
    component: React.lazy(() =>
      import("views/auth-views/authentication/lock-screen")
    ),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: "home.pages.homepage",
    path: `${HOME_PREFIX_PATH}/pages/homepage`,
    component: React.lazy(() => import("views/home-views/pages/homepage")),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: "home.pages.aboutus",
    path: `${HOME_PREFIX_PATH}/pages/aboutus`,
    component: React.lazy(() => import("views/home-views/pages/aboutus")),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: "home.pages.terms",
    path: `${HOME_PREFIX_PATH}/pages/terms`,
    component: React.lazy(() => import("views/home-views/pages/terms")),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: "home.pages.privacy",
    path: `${HOME_PREFIX_PATH}/pages/privacy`,
    component: React.lazy(() => import("views/home-views/pages/privacy")),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: "home.pages.register",
    path: `${HOME_PREFIX_PATH}/pages/register`,
    component: React.lazy(() => import("views/home-views/pages/register")),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: "home.pages.subscribe",
    path: `${HOME_PREFIX_PATH}/pages/subscribe`,
    component: React.lazy(() => import("views/home-views/pages/subscribe")),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: "home.pages.requisition",
    path: `${HOME_PREFIX_PATH}/pages/requisition`,
    component: React.lazy(() => import("views/home-views/pages/requisition")),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: "home.pages.revenform",
    path: `${HOME_PREFIX_PATH}/pages/revenform`,
    // CUTOVER (handoff §8.1): the canonical route renders the chooser, which picks
    // v1 (default) or v2 per org.revenform_version / env allowlist. PATH + key are
    // byte-identical so every printed barcode resolves the same; the chooser fails
    // SAFE to v1 on any uncertainty. v1 itself is never edited.
    component: React.lazy(() => import("views/home-views/pages/revenform-chooser")),
    meta: {
      blankLayout: true,
    },
  },
  {
    // revenform-v2 side-by-side TEST path — ALWAYS renders v2 (handoff §8.2).
    // Additive: the canonical /pages/revenform entry above is byte-untouched so
    // printed barcodes keep resolving. Lazy import keeps zod/RHF/libphonenumber
    // out of v1's chunk.
    key: "home.pages.revenform-v2",
    path: `${HOME_PREFIX_PATH}/pages/revenform-v2`,
    component: React.lazy(() => import("views/home-views/pages/revenform-v2")),
    meta: {
      blankLayout: true,
    },
  },
  {
    key: "home.pages.modaldisplay",
    path: `${HOME_PREFIX_PATH}/pages/modaldisplay`,
    component: React.lazy(() =>
      import("views/home-views/pages/modaldisplay/modalfroms")
    ),
    meta: {
      blankLayout: true,
    },
  },
];
